import { useEffect, useState } from "react";
import { Users } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useSegmentPreview } from "@/hooks/useNewsletter";
import type { SegmentRules } from "@shared/newsletterTypes";
import { MoneyInput } from "@/components/MoneyInput";
import { moneyInputValue, parseMoney } from "@shared/money";

const FIRM_TYPES = ["Private Equity", "Independent Sponsor", "Family Office", "Search Fund", "Strategic Acquirer", "Other"];

const csv = (v: string) => v.split(",").map((s) => s.trim()).filter(Boolean);

/**
 * An EBITDA bound. Commits on blur like the other fields, but only an amount
 * that parses cleanly: this used to be Number("3M"), which is NaN, which the
 * segment query then compared against every buyer.
 */
function EbitdaBound({ id, label, stored, placeholder, onCommit }: {
  id: string; label: string; stored: number | undefined; placeholder: string; onCommit: (v: number | undefined) => void;
}) {
  const [raw, setRaw] = useState(() => moneyInputValue(stored));
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <MoneyInput id={id} kind="buyerEbitda" value={raw} onValueChange={setRaw} placeholder={placeholder}
        onBlur={() => {
          const p = parseMoney(raw, "buyerEbitda");
          if (p.status === "empty") onCommit(undefined);
          else if (p.status === "ok") onCommit(p.dollars!);
        }} />
    </div>
  );
}

/**
 * Rules in, live count out.
 *
 * The count is the whole point of the screen: rules that quietly match nobody
 * (or the entire network) are the failure mode, and a number that moves as you
 * type is what surfaces it.
 */
export function SegmentBuilder({ rules, onChange }: { rules: SegmentRules; onChange: (r: SegmentRules) => void }) {
  const preview = useSegmentPreview();
  const [counts, setCounts] = useState<{ buyerCount: number; contactCount: number } | null>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      preview.mutateAsync(rules).then(setCounts).catch(() => setCounts(null));
    }, 350); // debounce: this runs a real query
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(rules)]);

  const set = <K extends keyof SegmentRules>(key: K, value: SegmentRules[K]) =>
    onChange({ ...rules, [key]: value });

  return (
    <div className="space-y-4">
      <div>
        <Label>Firm type</Label>
        <div className="flex flex-wrap gap-3 mt-1">
          {FIRM_TYPES.map((t) => (
            <label key={t} className="flex items-center gap-1.5 text-sm">
              <Checkbox
                checked={rules.firmTypes?.includes(t) ?? false}
                onCheckedChange={(v) =>
                  set("firmTypes", v
                    ? [...(rules.firmTypes ?? []), t]
                    : (rules.firmTypes ?? []).filter((x) => x !== t))
                }
              />
              {t}
            </label>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Buyers with no firm type recorded are excluded when this is set.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <EbitdaBound id="seg-ebitda-min" label="EBITDA from" stored={rules.ebitdaMin} placeholder="3M"
          onCommit={(v) => set("ebitdaMin", v)} />
        <EbitdaBound id="seg-ebitda-max" label="EBITDA to" stored={rules.ebitdaMax} placeholder="10M"
          onCommit={(v) => set("ebitdaMax", v)} />
      </div>

      <div>
        <Label htmlFor="seg-industries">Industries (comma separated)</Label>
        <Input id="seg-industries" defaultValue={(rules.industries ?? []).join(", ")} placeholder="Healthcare, Home Health"
          onBlur={(e) => set("industries", csv(e.target.value))} />
      </div>

      <div>
        <Label htmlFor="seg-geos">Geographies (comma separated)</Label>
        <Input id="seg-geos" defaultValue={(rules.geographies ?? []).join(", ")} placeholder="Texas, Oklahoma"
          onBlur={(e) => set("geographies", csv(e.target.value))} />
      </div>

      <label className="flex items-center gap-2 text-sm">
        <Checkbox checked={rules.engagedOnly ?? false} onCheckedChange={(v) => set("engagedOnly", !!v)} />
        Only buyers we have spoken to
      </label>

      <div className="flex items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2">
        <Users className="w-4 h-4 text-muted-foreground" />
        <p className="text-sm tabular-nums">
          {counts
            ? `${counts.buyerCount} buyers / ${counts.contactCount} contacts`
            : "Counting..."}
        </p>
      </div>
      <p className="text-xs text-muted-foreground">
        Ranges match on overlap, so a buyer stating $2M to $8M matches a $5M to $15M rule.
        Buyers with no criteria on file still match, apart from firm type.
      </p>
    </div>
  );
}
